// Anonymisation des contenus envoyés à l'IA (déplacée depuis
// reviewedFollowupAdaptation.js pour le lot 7 du suivi, comportement inchangé).
// Aucune identité (prénom, nom, e-mail, téléphone, adresse, date de
// naissance, identifiant) ne doit partir vers le modèle.

export const IDENTITY_KEYS = Object.freeze([
  Object.freeze({ key: 'prenom', minLength: 2 }),
  Object.freeze({ key: 'firstName', minLength: 2 }),
  Object.freeze({ key: 'firstname', minLength: 2 }),
  Object.freeze({ key: 'first_name', minLength: 2 }),
  Object.freeze({ key: 'nom', minLength: 2 }),
  Object.freeze({ key: 'lastName', minLength: 2 }),
  Object.freeze({ key: 'lastname', minLength: 2 }),
  Object.freeze({ key: 'last_name', minLength: 2 }),
  Object.freeze({ key: 'email', minLength: 2 }),
  Object.freeze({ key: 'telephone', minLength: 2 }),
  Object.freeze({ key: 'phone', minLength: 2 }),
  Object.freeze({ key: 'mobile', minLength: 2 }),
  Object.freeze({ key: 'mobilePhone', minLength: 2 }),
  Object.freeze({ key: 'mobile_phone', minLength: 2 }),
  Object.freeze({ key: 'adresse', minLength: 2 }),
  Object.freeze({ key: 'address', minLength: 2 }),
  Object.freeze({ key: 'dateNaissance', minLength: 2 }),
  Object.freeze({ key: 'date_naissance', minLength: 2 }),
  Object.freeze({ key: 'birthdate', minLength: 2 }),
  Object.freeze({ key: 'dateOfBirth', minLength: 2 }),
]);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function collectIdentityValues(...sources) {
  const values = [];
  for (const source of sources) {
    for (const { key, minLength } of IDENTITY_KEYS) {
      const value = typeof source?.[key] === 'string' ? source[key].trim() : '';
      if (value && value.length < minLength) {
        throw new Error('À valider Anissa — une valeur d’identité est trop courte pour être anonymisée de façon sûre.');
      }
      if (value.length >= minLength) values.push(value);
    }
  }
  return [...new Set(values)].sort((a, b) => b.length - a.length);
}

function identityPattern(identity) {
  const escaped = escapeRegExp(identity);
  if (/^[\p{L}\p{N}'’_-]+$/u.test(identity)) {
    return new RegExp(`(^|[^\\p{L}\\p{N}])(${escaped})(?=$|[^\\p{L}\\p{N}])`, 'giu');
  }
  return new RegExp(escaped, 'giu');
}

export function containsIdentity(value, identity) {
  return identityPattern(identity).test(value);
}

export function redactText(value, identityValues) {
  let result = String(value ?? '');
  for (const identity of identityValues) {
    const pattern = identityPattern(identity);
    result = result.replace(pattern, (match, prefix) => (
      /^[\p{L}\p{N}'’_-]+$/u.test(identity)
        ? `${prefix || ''}[IDENTITÉ RETIRÉE]`
        : '[IDENTITÉ RETIRÉE]'
    ));
  }
  return result
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/giu, '[EMAIL RETIRÉ]')
    .replace(/(?:\+?\d[\d .()/-]{7,}\d)/g, '[TÉLÉPHONE RETIRÉ]');
}

export function redactDeep(value, identityValues) {
  if (typeof value === 'string') return redactText(value, identityValues);
  if (Array.isArray(value)) return value.map((item) => redactDeep(item, identityValues));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, redactDeep(item, identityValues)]));
  }
  return value;
}

export function assertFollowupAiPromptAnonymized(value, identityValues = []) {
  const serialized = typeof value === 'string' ? value : JSON.stringify(value);
  if (/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/iu.test(serialized)
      || /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/iu.test(serialized)) {
    throw new Error('À valider Anissa — le brouillon contient encore un identifiant personnel.');
  }
  const phoneCandidates = serialized.match(/\+?\d[\d .()/-]{7,}\d/gu) || [];
  if (phoneCandidates.some((candidate) => candidate.replace(/\D/g, '').length >= 9)) {
    throw new Error('À valider Anissa — le brouillon contient encore un numéro de téléphone.');
  }
  if (identityValues.some((identity) => containsIdentity(serialized, identity))) {
    throw new Error('À valider Anissa — une valeur d’identité reste présente dans le brouillon.');
  }
  return true;
}
